import React from "react";

import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Checkbox from '@mui/material/Checkbox';
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../../hooks/useSnackbar";

interface IProps {
  label?: string;
  options?: any[];
  isCheckbox?: boolean;
  defaultValue: string | boolean | null;
  onEditSuccess?: (newValue: any) => Promise<void>;
  deleteButton?: React.ReactNode;
  maxValue?: number;
}

export const EditWrapper: React.FC<IProps> = (props) => {
  const { showErrorSnackbar } = useSnackbar();
  const [isEditState, setIsEditState] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [value, setValue] = React.useState<string | boolean>(props.defaultValue || "");
  const [checkboxValue, setCheckboxValue] = React.useState<boolean>(props.defaultValue as boolean);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setValue(e.target.value);
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCheckboxValue(e.target.checked);

  const toogleEditState = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    setIsEditState((prevState) => !prevState);
  };

  const handleChexkboxEditSuccess = React.useCallback(
    async (e) => {
      e.stopPropagation();

      if (isLoading) {
        return;
      }

      if (checkboxValue === props.defaultValue) {
        return setIsEditState(false);
      }

      if (checkboxValue === undefined) {
        return;
      }

      if (props.onEditSuccess) {
        setIsLoading(true);
        try {
          await props.onEditSuccess(checkboxValue);
        } catch (error) {
          showErrorSnackbar("An error occurred while saving");
        } finally {
          setIsLoading(false);
        }
      }

      setIsEditState(false);
    },
    [checkboxValue, props.defaultValue, isLoading]
  );

  const handleEditSuccess = React.useCallback(
    async (e) => {
      e.stopPropagation();

      if (isLoading) {
        return;
      }

      if (value === props.defaultValue) {
        return setIsEditState(false);
      }

      if (value === "") {
        return;
      }

      if (props.onEditSuccess) {
        setIsLoading(true);
        try {
          if (props.maxValue && (Number(value) > props.maxValue)) {
            showErrorSnackbar("Unit size can’t be bigger than " + props.maxValue);
            setValue(props.maxValue.toString());
            await props.onEditSuccess(props.maxValue);
          } else {
            await props.onEditSuccess(value);
          }
        } catch (error) {
          showErrorSnackbar("An error occurred while saving");
        } finally {
          setIsLoading(false);
        }
      }

      setIsEditState(false);
    },
    [value, props.defaultValue, isLoading]
  );

  const onInputClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
    e.stopPropagation();

  

  return (
    <>
      {!isEditState && (
        <Grid container spacing={2}>
          <Grid item>{props.children}</Grid>
          <Grid item xs>
            <IconButton size="small" onClick={toogleEditState}>
              <EditIcon />
            </IconButton>
              {props.deleteButton && (props.deleteButton)}
          </Grid>
        </Grid>
      )}
      {isEditState && (
        <Grid container spacing={2}>
          <Grid item>

            {props.isCheckbox &&
            <>
              <Typography>{props.label} 
              <Checkbox
                checked={checkboxValue}
                onChange={handleCheckboxChange}
              />
              </Typography>
              
            </>}
            {!props.isCheckbox && !props.options?.length && !props.maxValue && (
              <TextField
                variant="standard"
                autoFocus
                value={value}
                onClick={onInputClick}
                onChange={handleChange}
              />
            )}
            {!props.isCheckbox && !props.options?.length && props.maxValue && (
              <TextField
                variant="standard"
                type="number"
                autoFocus
                value={value}
                onClick={onInputClick}
                onChange={handleChange}
              />
            )}
            {!props.isCheckbox && !!props.options?.length && (
              <TextField
                select
                autoFocus
                size="small"
                variant="standard"
                value={value}
                onClick={onInputClick}
                onChange={handleChange}
                sx={{ width: 100 }}
              >
                {props.options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs>
            <IconButton
              size="small"
              onClick={props.isCheckbox? handleChexkboxEditSuccess : handleEditSuccess}
              disabled={isLoading}
            >
              <DoneIcon color="secondary" />
            </IconButton>
            <IconButton
              size="small"
              onClick={toogleEditState}
              disabled={isLoading}
            >
              <CloseIcon color="error" />
            </IconButton>
          </Grid>
        </Grid>
      )}
    </>
  );
};

EditWrapper.defaultProps = {
  defaultValue: "",
};
